// =============================================================================
// UrnaView.ts — A Urna Eletrônica Hackeada: 3 opções, voto com 1/2/3, timer do
// voto compulsório, os poderes da mesa diretora (issue #100: Recontagem de
// Votos, Cassação de Mandato e Abstenção, com contador de cargas), o card
// dourado "Emenda de Relator" (issue #93) e o overlay da pausa democrática
// (outro jogador votando). Ícones 100% pixel art (issue #31) — zero emoji.
// O card ainda mostra a RECEITA DE EVOLUÇÃO (#98): qual passiva evolui a arma
// (e qual arma a passiva evolui) — pro eleitor planejar a build.
// =============================================================================
import {
  URNA_GOLDEN_CHANCE, URNA_GOLDEN_MULT, URNA_SKIP_HEAL_FRAC, URNA_SKIP_SCORE,
} from '../../../shared/constants';
import type {
  PauseInfo, UrnaActionKind, UrnaCharges, UrnaChoiceDisplay,
} from '../../../shared/protocol';
import { MAX_WEAPON_LEVEL, WEAPON_TABLE } from '../../../shared/weapons';
import { UPGRADE_TABLE } from '../../../shared/upgrades';
import { goldenSealIcon, urnaActionIcon } from './pixelIcons';
import {
  renderForgeCard, loadCardImage, CARD_LOGICAL, CARD_H_LOGICAL, type ForgeCardConfig,
} from './cardForge';
import { resolveCardArt, CARD_ART } from './cardArt';
import { audio } from '../AudioEngine';
import { $ } from './dom';

// Backing store do canvas do card: 2x a resolução lógica (300x468) pra manter
// a pixel art nítida ao ampliar. O tamanho visual é controlado por CSS.
const CARD_CANVAS_SCALE = 2;

// Roleta de abertura (issue nova): os 3 slots giram por faces aleatórias e
// travam da esquerda pra direita. Tempo de travamento por slot (ms).
const ROLL_BASE_MS = 460;
const ROLL_STEP_MS = 300;

// Pool de faces pra girar: TODAS as cartas do jogo, resolvidas uma vez. A arte
// (src) fica separada pra checar, a cada troca, se JÁ carregou — o giro só mostra
// cartas com arte pronta, nunca o placeholder "[ arte aqui ]".
type Face = Omit<ForgeCardConfig, 'img'> & { image: string };
let FACE_POOL: Face[] | null = null;
function facePool(): Face[] {
  if (FACE_POOL) return FACE_POOL;
  FACE_POOL = Object.keys(CARD_ART).map((id) => {
    const w = WEAPON_TABLE[id];
    const u = UPGRADE_TABLE[id];
    const art = resolveCardArt({
      kind: w ? 'weapon' : 'upgrade',
      id,
      nome: w?.nome ?? u?.nome ?? id,
      desc: w?.desc ?? u?.desc ?? '',
      tipo: w?.tipo,
    });
    loadCardImage(art.image, () => { /* pré-carrega desde o boot */ });
    return {
      title: art.title, tag: art.tag, desc: art.desc, theme: art.theme,
      gemTop: art.gemTop, gemCorner: art.gemCorner, gemL: art.gemL, gemR: art.gemR,
      image: art.image,
    };
  });
  return FACE_POOL;
}

// Sorteia uma face cuja arte JÁ carregou (evita placeholder no giro). Tenta
// algumas vezes; devolve null se nada estiver pronto ainda (aí não troca).
function randomLoadedFace(pool: Face[]): ForgeCardConfig | null {
  for (let tries = 0; tries < 8; tries++) {
    const f = pool[(Math.random() * pool.length) | 0];
    const img = loadCardImage(f.image, () => { /* noop */ });
    if (img) return { ...f, img };
  }
  return null;
}

// Receita de evolução do card (#98): arma mostra a passiva que a evolui;
// passiva mostra as armas que ela evolui. Dados locais — nada viaja na rede.
function recipeHint(c: UrnaChoiceDisplay): string {
  if (c.kind === 'weapon') {
    const def = WEAPON_TABLE[c.id];
    const evo = def?.evolution;
    if (!evo || def?.evolved) return '';
    const passiva = UPGRADE_TABLE[evo.passive]?.nome ?? evo.passive;
    const suprema = WEAPON_TABLE[evo.into]?.nome ?? evo.into;
    return `RECEITA: nv ${MAX_WEAPON_LEVEL} + ${passiva} → ${suprema} (na maleta)`;
  }
  const alvos = Object.values(WEAPON_TABLE)
    .filter((w) => !w.evolved && w.evolution?.passive === c.id);
  if (!alvos.length) return '';
  return alvos
    .map((w) => `EVOLUI ${w.nome} nv ${MAX_WEAPON_LEVEL} → ${WEAPON_TABLE[w.evolution!.into]?.nome ?? ''}`)
    .join(' · ');
}

export interface UrnaHandlers {
  onPick(idx: number): void;
  onAction(action: UrnaActionKind, idx?: number): void;
}

// Rótulos dos poderes da mesa diretora (contador de cargas vem do servidor)
const ACOES: { action: UrnaActionKind; label: string; hint: string }[] = [
  { action: 'reroll', label: 'RECONTAGEM DE VOTOS', hint: 'sorteia 3 opções novas' },
  { action: 'banish', label: 'CASSAÇÃO DE MANDATO', hint: 'apaga uma opção do pool até o fim' },
  {
    action: 'skip', label: 'ABSTENÇÃO',
    hint: `recusa e cura ${Math.round(URNA_SKIP_HEAL_FRAC * 100)}% do HP (caído: +${URNA_SKIP_SCORE} pts)`,
  },
];

export class UrnaView {
  private handlers: UrnaHandlers | null = null;
  private choiceCount = 0;
  private banishMode = false;
  // Ação enviada ao servidor: trava a urna até a resposta chegar (nova oferta,
  // urnaSkip ou urnaResult) — evita gasto duplo e voto em card trocado
  private locked = false;
  // Timer local: conta a partir do prazo anunciado na oferta (cada jogador tem
  // o SEU prazo — a recontagem reseta só o seu, então o snapshot não serve)
  private deadline = 0;
  private timerLoop: ReturnType<typeof setInterval> | null = null;
  // Roleta de abertura: os slots (canvas + carta final) e o estado do giro
  private slots: { canvas: HTMLCanvasElement; art: ReturnType<typeof resolveCardArt> }[] = [];
  private rolling = false;
  private rollRAF = 0;
  // Cartas deitadas no mobile em pé (empilhadas). Decidido a cada abertura.
  private horizontal = false;

  constructor() {
    // Pré-carrega as artes desde o boot pra roleta nunca girar com placeholder
    facePool();
  }

  show(choices: UrnaChoiceDisplay[], charges: UrnaCharges, handlers: UrnaHandlers): void {
    this.handlers = handlers;
    this.locked = false;
    this.setBanishMode(false);
    this.choiceCount = choices.length;
    this.horizontal = window.matchMedia('(max-width: 700px) and (orientation: portrait)').matches;
    $('urna').classList.toggle('deitado', this.horizontal);
    this.slots = [];
    const box = $('urna-choices');
    box.innerHTML = '';
    // Só reserva a faixa do selo (padding no topo das 3 cartas) quando há uma
    // Emenda no set — no roll normal as cartas ficam justas, sem espaço morto.
    box.classList.toggle('tem-dourada', choices.some((c) => c.golden));
    choices.forEach((c, i) => {
      box.appendChild(this.buildCard(c, i));
    });
    this.renderActions(charges);
    $('urna').style.display = '';
    this.startRoll();
  }

  // Monta um card da urna: moldura/gemas desenhadas por código (cardForge) +
  // overlays de DOM (número pra votar no teclado, selo dourado e a receita de
  // evolução). O título/descrição/tag/cores vêm da config central (cardArt).
  private buildCard(c: UrnaChoiceDisplay, i: number): HTMLElement {
    const el = document.createElement('div');
    el.className = 'urna-choice' + (c.golden ? ' dourada' : '');

    const canvas = document.createElement('canvas');
    canvas.className = 'forge-card' + (this.horizontal ? ' deitado' : '');
    const L = this.horizontal ? CARD_H_LOGICAL : CARD_LOGICAL;
    canvas.width = L.w * CARD_CANVAS_SCALE;
    canvas.height = L.h * CARD_CANVAS_SCALE;
    el.appendChild(canvas);

    const nome = c.nome + ((c.lvl ?? 1) > 1 ? ` nv ${c.lvl}` : '');
    const tipo = c.kind === 'weapon' ? WEAPON_TABLE[c.id]?.tipo : undefined;
    const art = resolveCardArt({
      kind: c.kind, id: c.id, nome, desc: c.desc, tipo, golden: c.golden,
    });
    this.slots.push({ canvas, art });
    this.paintCard(canvas, art);   // pinta o final de cara (fallback se o giro não rolar)

    // Número do voto (1/2/3) — overlay pra combinar com o atalho de teclado
    const num = document.createElement('div');
    num.className = 'num';
    num.textContent = String(i + 1);
    el.appendChild(num);

    // Emenda de Relator (issue #93): a chance real vai PUBLICADA no card
    if (c.golden) {
      const selo = document.createElement('div');
      selo.className = 'selo-dourado';
      selo.innerHTML = `${goldenSealIcon(14)}<span>EMENDA DE RELATOR</span>`
        + `<small>efeito ${String(URNA_GOLDEN_MULT).replace('.', ',')}x`
        + ` — chance real de ${Math.round(URNA_GOLDEN_CHANCE * 100)}%</small>`;
      el.appendChild(selo);
    }

    // Receita de evolução (#98): fica embaixo do card
    const hint = recipeHint(c);
    if (hint) {
      const h = document.createElement('div');
      h.className = 'evo-hint';
      h.textContent = hint;
      el.appendChild(h);
    }

    el.onclick = () => this.pick(i);
    return el;
  }

  // Desenha o card no canvas; se a arte ainda não carregou, carrega e re-desenha
  // esse canvas quando ficar pronta (placeholder xadrez enquanto isso).
  private paintCard(canvas: HTMLCanvasElement, art: ReturnType<typeof resolveCardArt>): void {
    const img = loadCardImage(art.image, () => this.paintCard(canvas, art));
    renderForgeCard(canvas, {
      title: art.title, tag: art.tag, desc: art.desc, theme: art.theme,
      gemTop: art.gemTop, gemCorner: art.gemCorner, gemL: art.gemL, gemR: art.gemR,
      img,
    }, this.horizontal);
  }

  // ---------------------------------------------------------------- roleta
  // Abre a urna girando os 3 slots por faces aleatórias e travando da esquerda
  // pra direita, com "chunk" a cada trava. Reduced-motion pula direto pro final;
  // qualquer clique/tecla durante o giro finaliza na hora (não vota).
  private startRoll(): void {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || this.slots.length === 0) { this.finishRoll(); return; }

    this.rolling = true;
    $('urna').classList.add('rolando');
    this.slots.forEach((s) => s.canvas.parentElement?.classList.add('spin'));

    const pool = facePool();
    const t0 = performance.now();
    const lockAt = this.slots.map((_, i) => ROLL_BASE_MS + i * ROLL_STEP_MS);
    const nextSwap = this.slots.map(() => 0);
    const locked = this.slots.map(() => false);
    let lastTick = 0;

    const frame = (now: number): void => {
      if (!this.rolling) return;
      const t = now - t0;
      let done = true;
      this.slots.forEach((s, i) => {
        if (locked[i]) return;
        if (t >= lockAt[i]) { locked[i] = true; this.lockSlot(i); return; }
        done = false;
        if (t >= nextSwap[i]) {
          const prog = t / lockAt[i];
          nextSwap[i] = t + 50 + prog * prog * 160;   // desacelera: ~50ms -> ~210ms
          const face = randomLoadedFace(pool);        // só cartas com arte pronta
          if (face) {
            renderForgeCard(s.canvas, face, this.horizontal);
            if (now - lastTick > 50) { audio.sfx.casinoTick(); lastTick = now; }
          }
        }
      });
      if (done) { this.endRoll(); return; }
      this.rollRAF = requestAnimationFrame(frame);
    };
    this.rollRAF = requestAnimationFrame(frame);
  }

  // Trava um slot na carta final com "chunk" + punch de escala
  private lockSlot(i: number): void {
    const s = this.slots[i];
    this.paintCard(s.canvas, s.art);
    const el = s.canvas.parentElement;
    el?.classList.remove('spin');
    el?.classList.add('lock');
    audio.sfx.urnaKey();
    window.setTimeout(() => el?.classList.remove('lock'), 380);
  }

  // Pinta todos os finais e revela os overlays (reduced-motion ou giro pulado)
  private finishRoll(): void {
    if (this.rollRAF) { cancelAnimationFrame(this.rollRAF); this.rollRAF = 0; }
    this.slots.forEach((s) => {
      s.canvas.parentElement?.classList.remove('spin');
      this.paintCard(s.canvas, s.art);
    });
    this.endRoll();
  }

  private endRoll(): void {
    this.rolling = false;
    if (this.rollRAF) { cancelAnimationFrame(this.rollRAF); this.rollRAF = 0; }
    $('urna').classList.remove('rolando');
  }

  // Botões da mesa diretora com contador de cargas (issue #100)
  private renderActions(charges: UrnaCharges): void {
    const box = $('urna-actions');
    box.innerHTML = '';
    for (const d of ACOES) {
      const count = charges[d.action];
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `urna-acao acao-${d.action}`;
      btn.disabled = count <= 0;
      btn.innerHTML = `${urnaActionIcon(d.action, 16)}`
        + `<span class="acao-txt"><b>${d.label}</b><small>${d.hint}</small></span>`
        + `<span class="acao-cargas">x${count}</span>`;
      btn.onclick = () => this.actionClick(d.action);
      box.appendChild(btn);
    }
  }

  private actionClick(action: UrnaActionKind): void {
    if (this.rolling) { this.finishRoll(); return; }   // giro em curso: só finaliza
    if (!this.handlers || this.locked) return;
    if (action === 'banish') {
      // Modo cassação: o PRÓXIMO clique num card cassa aquela opção
      this.setBanishMode(!this.banishMode);
      return;
    }
    this.setBanishMode(false);
    this.locked = true;   // o servidor responde com a nova oferta (reroll) ou fecha (skip)
    this.handlers.onAction(action);
  }

  private setBanishMode(on: boolean): void {
    this.banishMode = on;
    $('urna').classList.toggle('cassando', on);
  }

  pick(i: number): void {
    if (this.rolling) { this.finishRoll(); return; }   // giro em curso: só finaliza
    if (!this.handlers || this.locked || i < 0 || i >= this.choiceCount) return;
    if (this.banishMode) {
      this.setBanishMode(false);
      this.locked = true;   // a urna re-renderiza quando a oferta reposta chegar
      this.handlers.onAction('banish', i);
      return;
    }
    const h = this.handlers;
    this.hide();
    h.onPick(i);
  }

  visible(): boolean {
    return $('urna').style.display !== 'none';
  }

  // Fecha a urna sem votar (ex.: o servidor votou por você no timeout)
  hide(): void {
    this.rolling = false;
    if (this.rollRAF) { cancelAnimationFrame(this.rollRAF); this.rollRAF = 0; }
    this.handlers = null;
    this.locked = false;
    this.setBanishMode(false);
    this.stopTimer();
    const el = $('urna');
    el.classList.remove('rolando');
    el.style.display = 'none';
  }

  // Prazo do SEU voto: contagem local a partir do tempo anunciado na oferta
  setTimer(seconds: number): void {
    this.deadline = performance.now() + seconds * 1000;
    this.renderTimer();
    if (!this.timerLoop) this.timerLoop = setInterval(() => this.renderTimer(), 250);
  }

  private renderTimer(): void {
    const left = Math.max(0, Math.ceil((this.deadline - performance.now()) / 1000));
    $('urna-timer').textContent = left > 0 ? `${left}s` : '';
    $('urna-timer').classList.toggle('urgente', left <= 4);
  }

  private stopTimer(): void {
    if (this.timerLoop) clearInterval(this.timerLoop);
    this.timerLoop = null;
  }

  // Overlay de pausa: outro jogador na urna (democrática) ou recesso via Esc
  // em partida solo (pause.manual)
  setPauseOverlay(pause: PauseInfo | null, youAreVoting: boolean): void {
    const el = $('pause-overlay');
    if (!pause || youAreVoting) { el.style.display = 'none'; return; }
    el.style.display = '';
    const manual = !!pause.manual;
    // Touch não tem Esc (issue #105): a dica de tecla vira botão de retomar
    const touch = document.body.classList.contains('touch');
    $('pause-title').textContent = manual ? 'RECESSO PARLAMENTAR' : 'SESSÃO SUSPENSA';
    $('pause-urna-line').style.display = manual ? 'none' : '';
    $('pause-timer-line').style.display = manual ? 'none' : '';
    $('pause-esc-line').style.display = manual && !touch ? '' : 'none';
    $('pause-resume-btn').style.display = manual && touch ? '' : 'none';
    $('pause-quit-btn').style.display = manual ? '' : 'none';
    if (!manual) {
      $('pause-names').textContent = pause.names.join(', ');
      $('pause-left').textContent = `${pause.left}s`;
    }
  }

  // Botão do recesso solo: renuncia ao mandato e volta pro menu principal
  bindQuit(onQuit: () => void): void {
    $('pause-quit-btn').onclick = onQuit;
  }

  // Botão de retomar por toque (issue #105): mesmo game:pause do Esc
  bindResume(onResume: () => void): void {
    $('pause-resume-btn').onclick = onResume;
  }
}
